import { bool, shape } from 'prop-types';
import React, { PureComponent } from 'react';
import { ScrollView } from 'react-native';

import { C, SHAPE } from '../../common';
import { DialogSuggestion, Loading, Navigation } from '../../components';
import { Consumer } from '../../context';
import { ENV, LAYOUT } from '../../reactor/common';
import { Motion } from '../../reactor/components';
import { preparePages, visibility } from './modules';
import Header from '../Header';
import Page from '../Page';
import onElTiempoDomain from '../../common/onElTiempoDomain';
import styles from './Story.style';

const { IS_NATIVE, IS_WEB, IS_SERVER } = ENV;
const { addEventListener, removeEventListener } = (IS_SERVER || IS_NATIVE) ? {} : window;
const KEY_EVENT = 'keypress';
const { MAX_PAGE_PRELOAD } = C;

class Story extends PureComponent {
  static propTypes = {
    dataSource: shape(SHAPE.STORY),
    visible: bool,
  };

  static defaultProps = {
    dataSource: undefined,
    visible: true,
  };

  constructor(props) {
    super(props);
    this.state = {
      pageIndex: 0,
      pages: preparePages(props.dataSource),
      progress: 0,
      scroll: 0,
    };
    if (IS_WEB && addEventListener) addEventListener(KEY_EVENT, this.onNextPage);
  }

  componentWillReceiveProps({ dataSource = {} }) {
    const { props: { dataSource: { id } = {} } } = this;

    if (dataSource.id === id) return;
    this.setState({
      pageIndex: 0, pages: preparePages(dataSource), progress: 0, scroll: 0,
    });
  }

  componentWillUnmount() {
    if (IS_WEB && removeEventListener) removeEventListener(KEY_EVENT, this.onNextPage);
  }

  onNextPage = ({ keyCode } = {}) => {
    if (keyCode !== 13) return;
    const { onScrollMove, state: { pages = [], scroll } } = this;

    pages.some(({ y }, index) => {
      const nextPage = (scroll + 64) < y;
      if (nextPage) onScrollMove(index);
      return nextPage;
    });
  }

  onLayout = (index, { height, y } = {}) => {
    const { state: { pages = [] } } = this;
    const page = pages[index];

    if (page.height !== height || page.y !== y) {
      pages[index] = { ...page, height, y };
      this.setState({ pages });
    }
  }

  onLoad = (index) => {
    const { state: { pages = [] } } = this;

    pages[index].loaded = true;
    const progress = pages.length > 0
      ? pages.filter(({ loaded }) => loaded).length / pages.length
      : 0;

    this.setState({ pages, progress });
  }

  onScroll = ({ nativeEvent: { contentOffset }, ...inherit }) => {
    const { state: { scroll: currentScroll, pages = [], pageIndex } } = this;
    const scroll = parseInt(contentOffset.y, 10);

    if (currentScroll !== scroll) {
      const pagesVisibility = visibility({ pages, scroll, ...inherit });
      const page = pagesVisibility.find(({ y }) => y >= scroll);

      this.setState({
        pageIndex: page ? page.index : pageIndex,
        pages: pagesVisibility,
        scroll,
      });
    }
  }

  onScrollMove = (index) => {
    const { scrollview, state: { pages = [] } } = this;
    scrollview.scrollTo({ y: pages[index].y });
  }

  render() {
    const {
      onScrollMove, onLayout, onLoad, onScroll,
      props: { dataSource, visible },
      state: {
        pageIndex = 0, pages = [], progress,
      },
    } = this;
    const { suggestion } = dataSource || {};
    const ready = progress === 1;

    return (
      <Consumer>
        {({ logScroll }) => (
          <Motion
            style={styles.container}
            timeline={[{ property: 'scale', value: visible ? 1 : 0.75 }]}
          >
            { !onElTiempoDomain() && (
              <Header scrolling={pageIndex > 0} visible={ready} />
            )}
            { suggestion
              && (
              <DialogSuggestion
                dataSource={suggestion}
                visiblePages={pages.map((page, index) => (page.visibility > 0.5 ? index : undefined))}
              />
              ) }

            <ScrollView
              id="story"
              onScroll={({ nativeEvent = {} }) => onScroll({ nativeEvent, logScroll })}
              ref={(scrollview) => { this.scrollview = scrollview; }}
              scrollEnabled={ready}
              scrollEventThrottle={400}
              contentContainerStyle={styles.contentContainer}
              style={[styles.content, visible && styles.visible]}
            >
              { !IS_SERVER && <Loading progress={!dataSource ? 0 : progress} /> }
              { dataSource && dataSource.pages.map((data, index) => {
                const page = pages[index];

                return (
                  <Page
                    data={{ ...data, nextPage: dataSource.pages[index + 1] }}
                    key={data.id}
                    index={index}
                    onLayout={props => onLayout(index, props)}
                    onLoad={!IS_SERVER && index <= MAX_PAGE_PRELOAD ? () => onLoad(index) : undefined}
                    onPress={onScrollMove}
                    viewed={page.viewed}
                    visibility={page.visibility}
                  />
                );
              }) }
            </ScrollView>

            { LAYOUT.VIEWPORT.LANDSCAPE && (
              <Navigation pageIndex={pageIndex} pages={pages} onPress={onScrollMove} visible={pageIndex > 0} />)}
          </Motion>
        )}
      </Consumer>
    );
  }
}


export default Story;
